import { lazy, type ComponentType } from 'react';

/** Пометка о том, что перезагрузка уже была: она нужна ровно одна. */
const RELOAD_FLAG = 'aurea:chunk-reload';

/**
 * Ленивая загрузка страницы, переживающая выкат новой версии.
 *
 * Экраны кабинетов подгружаются по требованию, и имя файла содержит хэш
 * содержимого. Если во время работы вышла новая версия, открытая вкладка
 * держит прежний index.html и просит файл со старым именем — а такого файла
 * в новом релизе уже нет. Загрузка не завершается никогда, и человек видит
 * пустой экран, пока не обновит страницу сам.
 *
 * Поэтому неудачную загрузку разбираем сами: один раз перезагружаем страницу,
 * после чего index.html свежий и имена совпадают. Повторную перезагрузку
 * не делаем — если дело не в выкате, кольцо из перезагрузок только скрыло бы
 * настоящую причину.
 */
export function lazyPage<T extends ComponentType<unknown>>(
  load: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      const module = await load();
      sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    } catch (error) {
      if (sessionStorage.getItem(RELOAD_FLAG)) throw error;

      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();

      // Страница уже уходит на перезагрузку: ждём её, ничего не показывая.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
